import { Autocomplete, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, Switch, TextField } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import BooleanSelect from "../common/BooleanSelect";
import HelpAdornment from "../common/HelpAdornment";
import JobFrequencySelect from "../common/JobFrequencySelect";
import JobPlatformSelect from "../common/JobPlatformSelect";
import LanguageSelect from "../common/LanguageSelect";
import ProjectSelect from "../common/ProjectSelect.js.js";
import languages from "../../config/languages";
import { webSearchFieldValidation, platformFieldsValidation } from "../../validator/job";

function JobForm({form, onChange, errors, hideAdvancedFields=false}){
    const jobForm = form || {};
    const project = useSelector(state => state.project.single);
    const setJobForm = f => {
        onChange && onChange(f);
    }
    const searchHelpText = {
        // 'tw': "boolean operators search (AND, OR allowed); special operators from: to:",
        'web': "boolean operators search (AND, OR allowed)",
        'in': "boolean operators search (AND, OR allowed)",
        'inm': "one Linkedin username, internal ID or URN"
    }
    searchHelpText.wn = searchHelpText.wb = searchHelpText.wf = searchHelpText.web;

    const requiredPlatformFields = {
        nf: ['all'],
        nfo: ['all'],
        ig: ['all'],
        nftw: ['all'],
        tt: ['all'],
        tg: ['all'],
        bsky: ['all'],
        nc: ['authors'],
        ic: ['authors'],
        nftc: ['authors'],
        tc: ['authors'],
        tgc: ['channels'],
    }
    const platformFields = {
        nc: {
            authors: "Results are from any of these pages (FB page username or userid, comma separated)",
            excluded_authors: "Results are NOT from any of these pages (FB page username or userid, comma separated)"
        },
        ic: {
            authors: "Results are from any of these accounts (Instagram username, comma separated)",
            excluded_authors: "Results are NOT from any of these accounts (Instagram username, comma separated)"
        },
        nftc: {
            authors: "Results are from any of these accounts (X username, comma separated)",
            excluded_authors: "Results are NOT from any of these accounts (X username, comma separated)"
        },
        tc: {
            authors: "Results are from any of these accounts (TikTok username, comma separated)",
            excluded_authors: "Results are NOT from any of these accounts (TikTok username, comma separated)"
        },
        tgc: {
            channels: "Results are from any of these channels (Telegram channel id, comma separated)",
            excluded_channels: "Results are NOT from any of these channels (Telegram channel id, comma separated)"
        },
        nf: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these pages (FB page username or userid, comma separated)",
            excluded_authors: "Results are NOT from any of these pages (FB page username or userid, comma separated)"
        },
        nfo: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
        },
        ig: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these account (Instagram username, comma separated)",
            excluded_authors: "Results are NOT from any of these account (Instagram username, comma separated)"
        },
        nftw: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these account (X username, comma separated)",
            excluded_authors: "Results are NOT from any of these account (X username, comma separated)"
        },
        tt: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these account (TikTok username, comma separated)",
            excluded_authors: "Results are NOT from any of these account (TikTok username, comma separated)"
        },
        tg: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these account (TikTok username, comma separated)",
            excluded_authors: "Results are NOT from any of these account (TikTok username, comma separated)"
        },
        bsky: {
            all: "Results must contain all these terms (space separated, phrases in quotes)",
            one: "Results may contain one or more of these terms (space separated, phrases in quotes)",
            none: "Results don't contain any of these terms (space separated, phrases in quotes)",
            hashtags: "Results must contain all of these hashtags (space separated, omit the '#')",
            authors: "Results are from any of these account (TikTok username, comma separated)",
            excluded_authors: "Results are NOT from any of these account (TikTok username, comma separated)"
        },
    }

    const nfoPlatformOptions = useMemo(() => ([
        { value: "facebook", label: "Facebook" },
        { value: "instagram", label: "Instagram" },
        { value: "x", label: "X" },
        { value: "tiktok", label: "TikTok" },
    ]), []);

    const nfoDefaultOptions = useMemo(() => ({
        platforms: nfoPlatformOptions.map(p => p.value),
        language_codes: [],
    }), [nfoPlatformOptions]);

    const languageOptions = useMemo(() => Object.keys(languages).map(key => ({
        value: key,
        label: languages[key]
    })), []);

    useEffect(() => {
        if(!jobForm.type) return;

        // ensure searchJson exists for composed platforms
        if(platformFields[jobForm.type] && !jobForm.searchJson){
            setJobForm({
                ...jobForm,
                searchJson: {},
            });
            return;
        }

        // nfo-specific defaults for options
        if(jobForm.type !== 'nfo') return;
        const currentOptions = jobForm.options && typeof jobForm.options === 'object' ? jobForm.options : {};
        const currentPlatforms = Array.isArray(currentOptions.platforms) ? currentOptions.platforms : [];
        const currentLangs = Array.isArray(currentOptions.language_codes) ? currentOptions.language_codes : [];
        if(currentPlatforms.length === 0 || !Array.isArray(currentOptions.language_codes)){
            setJobForm({
                ...jobForm,
                options: {
                    ...nfoDefaultOptions,
                    ...currentOptions,
                    platforms: currentPlatforms.length ? currentPlatforms : nfoDefaultOptions.platforms,
                    language_codes: currentLangs,
                }
            });
        }
    }, [jobForm.type, jobForm.options, jobForm.searchJson, nfoDefaultOptions]);

    const nfoSelectedPlatforms = Array.isArray(jobForm?.options?.platforms) && jobForm.options.platforms.length
        ? jobForm.options.platforms
        : nfoDefaultOptions.platforms;
    const nfoSelectedLanguageCodes = Array.isArray(jobForm?.options?.language_codes) ? jobForm.options.language_codes : [];

    // Keep search (string) in sync for composed platforms so backend receives JSON in either field.
    useEffect(() => {
        if(!jobForm.type || !platformFields[jobForm.type]) return;
        const payloadJson = jobForm.searchJson || {};
        const encoded = JSON.stringify(payloadJson);
        if(jobForm.search !== encoded){
            setJobForm({
                ...jobForm,
                search: encoded,
            });
        }
    }, [jobForm.type, jobForm.searchJson, jobForm.search]);

    const handleNfoPlatformToggle = (value, checked) => {
        const currentOptions = jobForm.options && typeof jobForm.options === 'object' ? jobForm.options : {};
        const currentPlatforms = Array.isArray(currentOptions.platforms) && currentOptions.platforms.length
            ? currentOptions.platforms
            : nfoDefaultOptions.platforms;
        const nextPlatforms = checked
            ? Array.from(new Set([...currentPlatforms, value]))
            : currentPlatforms.filter(v => v !== value);
        setJobForm({
            ...jobForm,
            options: {
                ...currentOptions,
                platforms: nextPlatforms,
                language_codes: Array.isArray(currentOptions.language_codes) ? currentOptions.language_codes : []
            }
        });
    };

    const handleNfoLanguagesChange = (selected) => {
        const currentOptions = jobForm.options && typeof jobForm.options === 'object' ? jobForm.options : {};
        const codes = (selected || []).map(o => o.value);
        setJobForm({
            ...jobForm,
            options: {
                ...currentOptions,
                platforms: Array.isArray(currentOptions.platforms) ? currentOptions.platforms : nfoDefaultOptions.platforms,
                language_codes: codes,
            }
        });
    };
    

    return <Grid container spacing={4} columnSpacing={6}>
        <Grid item xs={12}>
            <TextField
                fullWidth
                required
                autoFocus
                error={!!errors.label}
                variant="standard"
                label="Job name"
                helperText={"The name you're identifying the job with"}
                value={jobForm.label || ""}
                onChange={e => setJobForm({...jobForm, label: e.target.value})}
                InputProps={{
                    endAdornment: <HelpAdornment helpKey={"jobLabel"} />
                }}

            />
        </Grid>
        
        <Grid item xs={6}>
            <JobPlatformSelect
                error={!!errors.type}
                helperText={"The platform to search"}
                value={jobForm.type}
                onChange={type => setJobForm({...jobForm, type})}

            />
        </Grid>
        <Grid item xs={6}>
            <JobFrequencySelect
                error={!!errors.freq}
                helperText={"How frequently the job must run"}
                required
                value={jobForm.freq}
                onChange={freq => setJobForm({...jobForm, freq})}

            />
        </Grid>
        {jobForm.type === 'nfo' && (
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl component="fieldset" error={!!errors.nfoPlatforms} variant="standard" fullWidth>
                            <FormGroup row>
                                {nfoPlatformOptions.map(opt => (
                                    <FormControlLabel
                                        key={opt.value}
                                        control={
                                            <Switch
                                                checked={nfoSelectedPlatforms.indexOf(opt.value) !== -1}
                                                onChange={(e, checked) => handleNfoPlatformToggle(opt.value, checked)}
                                            />
                                        }
                                        label={opt.label}
                                    />
                                ))}
                            </FormGroup>
                            {errors.nfoPlatforms ? (
                                <FormHelperText>{errors.nfoPlatforms}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            multiple
                            options={languageOptions}
                            getOptionLabel={(option) => option.label}
                            value={languageOptions.filter(o => nfoSelectedLanguageCodes.indexOf(o.value) !== -1)}
                            onChange={(e, newValue) => handleNfoLanguagesChange(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label="Languages"
                                    placeholder="Add languages (optional)"
                                    helperText="Leave empty to include all languages"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Grid>
        )}
        {jobForm.type && !platformFields[jobForm.type] && <Grid item xs={12}>
            <TextField
                fullWidth
                required
                error={!!errors.search || !!webSearchFieldValidation(jobForm.search)}
                variant="standard"
                label="Search"
                helperText={
                    webSearchFieldValidation(jobForm.search) 
                        ? webSearchFieldValidation(jobForm.search)
                        : searchHelpText[jobForm.type] || "The search string for your job"
                }
                value={jobForm.search || ""}
                onChange={e => setJobForm({...jobForm, search: e.target.value})}
                multiline
                rows={8}
                InputProps={{
                    endAdornment: <HelpAdornment helpKey={"jobSearch"} />
                }}
                sx={{
                    '& .MuiInput-root.Mui-error': {
                        '&:before': {
                            borderBottomColor: '#900',
                        },
                        '&:after': {
                            borderBottomColor: '#900',
                        },
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: '#900',
                    }
                }}
            />
        </Grid> }

        {jobForm.type && platformFields[jobForm.type] && <>
            {/* Show platform fields validation error if exists */}
            {platformFieldsValidation(jobForm.searchJson, jobForm.type) && (
                <Grid item xs={12}>
                    <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px', fontWeight:'800' }}>
                        {platformFieldsValidation(jobForm.searchJson, jobForm.type)}
                    </div>
                </Grid>
            )}
            {Object.keys(platformFields[jobForm.type]).map(key => {
                const label = platformFields[jobForm.type][key];
                const requiredArray = requiredPlatformFields[jobForm.type];
                const required = requiredArray.indexOf(key)!==-1;
                const hasValidationError = platformFieldsValidation(jobForm.searchJson, jobForm.type) && (key === 'all' || key === 'one');
                
                return <Grid sm={12} key={key} item>
                    <TextField
                        fullWidth
                        required={required}
                        error={hasValidationError}
                        variant="standard"
                        label={label}
                        multiline
                        rows={key === 'all' || key === 'one' || key === 'authors' ? 4 : 2}
                        
                        value={(jobForm.searchJson || {})[key] || ""}
                        onChange={e => setJobForm({...jobForm, searchJson: {...(jobForm.searchJson || {}), [key]: e.target.value}})}
                    />  
                </Grid>
            })}
        </>}

        <Grid item xs={6}>
            {/* hide language if Linkedin search or member or "all social" (nfo, language selector is upwards) */}
            {jobForm.type !== 'in' && jobForm.type !== 'inm' &&jobForm.type !== 'nfo' ? (
            <LanguageSelect
                error={!!errors.searchLang}
                // required
                helpKey="jobLanguage"
                value={jobForm.searchLang}
                onChange={searchLang => setJobForm({...jobForm, searchLang})}
            />
            ) : null}
        </Grid>
        {!hideAdvancedFields && <>
            <Grid item xs={6}>
                <BooleanSelect
                    required
                    label="Active"
                    helperText={"Activate the job"}
                    value={jobForm.active || false}
                    onChange={active => setJobForm({...jobForm, active})}

                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    error={!!errors.notes}
                    variant="standard"
                    label="Job notes"
                    helperText={""}
                    value={jobForm.notes || ""}
                    onChange={e => setJobForm({...jobForm, notes: e.target.value})}
                    InputProps={{
                        endAdornment: <HelpAdornment helpKey={"jobNotes"} />
                    }}

                />
            </Grid>
            {!project && <Grid item xs={12}>
                <ProjectSelect
                    fullWidth
                    error={!!errors.projects}
                    variant="standard"
                    label="Projects"
                    helperText={""}
                    value={jobForm.projects || []}
                    onChange={projects => setJobForm({...jobForm, projects})}
                />
            </Grid> }
        </>}
    </Grid>
}

export default JobForm;